/* Config */
import appConfig from '../../appConfig'

/* Utils */
import {getBrowserCookie} from '../../../client/common/persistence/browserCookieUtils'

const PRODUCT_STATUS_TYPES = {
  PRE_ORDER: 'Preorder',
  REMOVED: 'RemovedItem',
  OUT_OF_STOCK: 'OutOfStock',
  IN_STOCK: 'InStock',
};

const getProductFinalPrice = (prices) => {
  if (!prices?.length) {
    return null
  }

  const finalPriceIndex = prices.length - 1
  return prices[finalPriceIndex]?.value
}

const generateVariantStatus = (item) => {
  let result;

  if (item.inStockDate) {
    result = PRODUCT_STATUS_TYPES.PRE_ORDER;
  } else if (item.isProductLive) {
    if (item.procurable) {
      result = PRODUCT_STATUS_TYPES.IN_STOCK;
    } else {
      result = PRODUCT_STATUS_TYPES.OUT_OF_STOCK;
    }
  } else {
    result = PRODUCT_STATUS_TYPES.REMOVED;
  }

  return result;
}

const generateProductStatus = (product) => {
  let result;

  if (product?.isPreOrder) {
    result = PRODUCT_STATUS_TYPES.PRE_ORDER;
  } else if (product?.isProductLive) {
    if (product?.isProcurable || product?.stockInformation?.isInStock) {
      result = PRODUCT_STATUS_TYPES.IN_STOCK;
    } else {
      result = PRODUCT_STATUS_TYPES.OUT_OF_STOCK;
    }
  } else {
    result = PRODUCT_STATUS_TYPES.REMOVED;
  }

  return result;
}

const checkJetDeliveryLabelShown = ({location, cityBasedCutOffEnabled, jetDeliveryCities, townToTownEAD = []}) => {
  const cityCode = location?.city?.code;
  const receiverTownCode = location?.town?.code;
  const hasTownToTown = townToTownEAD?.some(city => city === cityCode);
  const currentCityOrTown = jetDeliveryCities?.find(city =>
    hasTownToTown && receiverTownCode ? city?.code === receiverTownCode : city?.code === cityCode
  );
  const hasCityCode = cityCode && currentCityOrTown;
  const cutOffHour = currentCityOrTown?.ykCutOff;

  if (cityBasedCutOffEnabled) {
    if (hasCityCode && Number.isInteger(cutOffHour)) {
      const currentDate = new Date();
      const currentHour = currentDate?.getHours() + 1;

      if (currentHour <= cutOffHour) {
        return true;
      }
    }
  } else if (hasCityCode) {
    return true
  }

  return false;
}

const generateOtherSellerCampaign = (sku, allListings) => {
  return {
    sku,
    merchantIds: allListings.map((listing) => listing?.merchantId) || [],
  }
}

const generateOtherSellerCoupon = (product) => {
  return {
    sku: product?.sku,
    merchantIdList: product?.listings?.map((listing) => listing?.merchantId) || [],
  }
}

const generateCampaignModel = (product, userInfo, platform, appKey) => {
  return {
    customer: {
      id: userInfo?.userId ? userInfo?.userId : getBrowserCookie('hbus_anonymousId'),
      tags: [],
      gsmNumber: null,
      uniqueDeviceId: null
    },
    platform: platform,
    appKey: appKey,
    product: {
      sku: product?.sku,
      productId: product?.productId,
      brand: product?.brand,
      definitionName: product?.definitionName,
      listingId: product?.listingId,
      productName: product?.name,
      merchant: {
        id: product?.merchant?.id,
        name: product?.merchant?.name,
        isCampaignContractAccepted: Boolean(product?.isCampaignParticipationClosed)
      },
      otherMerchants: product?.listings?.map((listing) => (
        {
          id: listing?.merchantId,
          name: listing?.merchantName,
          isCampaignContractAccepted: Boolean(listing?.isCampaignParticipationClosed)
        }
      )) || [],
      media: {
        linkFormat: product?.media?.[0]?.url,
        sortId: product?.media?.[0]?.showOrder,
        isDefault: true,
        width: product?.media?.[0]?.width,
        height: product?.media?.[0]?.height
      },
      productTags: product?.tagList?.map((tag) => tag?.tagId) || [],
      rootCategoryList: product?.categories?.map((category) => parseInt(category?.categoryId)) || [],
      url: `${appConfig.baseUrl}${window.location.pathname}${window.location.search}`,
      amount: getProductFinalPrice(product?.prices),
      productTagGroupNames: [], //
      campaignIds: product?.campaignIds
    }
  }
}

const handleViewEvent = (couponList) => {
  const merchantCoupons = couponList?.map((coupon, index) => {
    return {
      type: 'merchantCoupon',
      value: coupon?.couponId,
      position: `${index + 1}`,
      text: coupon?.couponName,
      code: coupon?.promoCode || coupon?.code,
      discountAmount: coupon?.discountAmount || coupon?.discount,
      conditionAmount: coupon?.conditionAmount,
      currentCount: coupon?.currentCount,
      merchantName: coupon?.merchantName,
      merchantId: coupon?.merchantId,
      couponType: coupon?.couponType,
      link: coupon?.link,
      couponId: coupon?.couponId,
      name: coupon?.couponName,
      usedDate: coupon?.usedDate,
      endDate: coupon?.endDate || coupon.endDateTime,
      campaignType: coupon?.campaignType,
      isCollected: coupon?.isCollected
    };
  });

  const categoryNameHierarchy = window?.utagData?.category_name_hierarchy;
  const categoryIdHierarchy = window?.utagData?.category_id_hierarchy;

  window?.hepsiBus?.track?.('couponsView', {
    location: 'ProductDetailPage',
    page_type: 'pdp',
    placement_id: 'MerchantCoupons',
    placement_title: 'Kuponlar',
    sku: window?.productModel?.product?.sku,
    categoryNameHierarchy,
    categoryIdHierarchy,
    items: merchantCoupons
  });
}

const promoCodeCouponsViewEvent = (coupons, sku, categoryNameHierarchy, categoryIdHierarchy) => {
  const promoCodeCoupons = coupons?.map((coupon, index) => {
    const {
      campaignId,
      campaignType,
      conditionAmount,
      couponType,
      discount,
      endDateTime,
      name
    } = coupon;
    return {
      type: 'promoCodeCoupon',
      position: `${index + 1}`,
      name,
      discountAmount: campaignType !== 1 ? discount : null,
      discountRate: campaignType === 1 ? discount : null,
      endDateTime,
      couponType,
      conditionAmount,
      campaignId
    };
  });

  window?.hepsiBus?.track?.('couponsView', {
    location: 'ProductDetailPage',
    page_type: 'pdp',
    placement_id: 'PromoCodeCoupons',
    placement_title: 'Kuponlar',
    sku,
    categoryNameHierarchy,
    categoryIdHierarchy,
    items: promoCodeCoupons
  });
};

const gotoMyCouponsClickEvent = () => {
  window?.hepsiBus?.track?.('LinkClick', {
    location: 'specialCampaignPage',
    page_type: 'Congratulations',
    page_value: '',
    placement: 'congratulations_screen_my_coupons_button',
    text: 'Kuponlarıma git'
  });
};


export {
  checkJetDeliveryLabelShown,
  generateProductStatus,
  generateVariantStatus,
  generateOtherSellerCampaign,
  generateOtherSellerCoupon,
  generateCampaignModel,
  handleViewEvent,
  gotoMyCouponsClickEvent,
  promoCodeCouponsViewEvent,
}
